// contexts/AuthContext.tsx - Updated for your backend
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest } from '../types';
import { apiService } from '../services/api.service';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Set token in service before making profile request
        apiService.setToken(token);
        const profile = await apiService.getProfile();
        setUser(profile);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      apiService.removeToken();
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      const response = await apiService.login(credentials);
      
      // Transform the user data from the login response
      const userData: User = {
        id: response.user.id,
        name: response.user.name,
        email: response.user.email,
        role: response.user.role as any, // Will be transformed by API service
        status: response.user.status as any,
        employee_id: response.user.employee_id,
        department: response.user.department,
        designation: response.user.designation,
        email_verified: true, // Default from login
        createdAt: new Date(), // Will be updated by profile fetch
        updatedAt: new Date(),
      };
      
      setUser(userData);
      
      // Optionally fetch full profile data
      try {
        const fullProfile = await apiService.getProfile();
        setUser(fullProfile);
      } catch (profileError) {
        console.warn('Failed to fetch full profile:', profileError);
        // Keep the basic user data from login response
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = (): void => {
    apiService.logout();
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};